import * as THREE from 'three'
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader.js'
import {DRACOLoader} from 'three/examples/jsm/loaders/DRACOLoader.js'
import type {RawAnchorAsset} from '../api/types'
import {assetUrl, isEnvironmentScan, resolveAssetKind} from './mapping'

const dracoLoader = new DRACOLoader().setDecoderPath('/draco/')
const gltfLoader = new GLTFLoader().setDRACOLoader(dracoLoader)
const textureLoader = new THREE.TextureLoader()

export interface BuiltAsset {
  object: THREE.Object3D
  kind: string
  dispose: () => void
}

function disposeObject(root: THREE.Object3D) {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (mesh.geometry) mesh.geometry.dispose()
    const mats = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : []
    for (const m of mats) {
      for (const v of Object.values(m)) if (v instanceof THREE.Texture) v.dispose()
      m.dispose()
    }
  })
}

async function buildModel(url: string): Promise<BuiltAsset> {
  const gltf = await gltfLoader.loadAsync(url)
  const object = gltf.scene
  // Face the model the way the Unity app does. Unity's glTFast converts glTF
  // (right-handed) to Unity (left-handed) by mirroring X, so a model's +Z
  // forward stays +Z = NORTH under the EUN terrain anchor. three renders the
  // raw glTF in a frame whose +Z is south (position z = -z_offset), and a
  // Z-mirror vs an X-mirror differ by exactly a 180° yaw — without this every
  // model faced the opposite way from the app. Yaw itself is applied to the
  // parent holder, so this child rotation composes with it. Model-only:
  // image/video planes already face south in both.
  object.rotation.y = Math.PI
  return {object, kind: 'model', dispose: () => disposeObject(object)}
}

async function buildImage(url: string): Promise<BuiltAsset> {
  const texture = await textureLoader.loadAsync(url)
  texture.colorSpace = THREE.SRGBColorSpace
  const img = texture.image as {width: number; height: number}
  const aspect = img && img.height > 0 ? img.width / img.height : 1
  // Unit height; uniform `size` scales it to meters.
  const geo = new THREE.PlaneGeometry(aspect, 1)
  const mat = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    side: THREE.DoubleSide,
  })
  const object = new THREE.Mesh(geo, mat)
  return {object, kind: 'image', dispose: () => disposeObject(object)}
}

/**
 * Floating name label — used as fallback content for checkpoints whose assets
 * are all skipped on web (audio/video-only), so "found" never means empty air.
 */
export function buildLabelSprite(text: string): BuiltAsset {
  const built = buildText(text)
  built.object.position.y = 1.5
  return built
}

function buildText(text: string): BuiltAsset {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  const font = '600 64px system-ui, sans-serif'
  ctx.font = font
  const pad = 24
  const metrics = ctx.measureText(text)
  canvas.width = Math.ceil(metrics.width) + pad * 2
  canvas.height = 64 + pad * 2
  const ctx2 = canvas.getContext('2d')!
  ctx2.font = font
  ctx2.fillStyle = 'rgba(12, 15, 20, 0.55)'
  ctx2.beginPath()
  ctx2.roundRect(0, 0, canvas.width, canvas.height, 20)
  ctx2.fill()
  ctx2.fillStyle = '#ffffff'
  ctx2.textBaseline = 'middle'
  ctx2.fillText(text, pad, canvas.height / 2)
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  const mat = new THREE.SpriteMaterial({map: texture, transparent: true})
  const sprite = new THREE.Sprite(mat)
  // Unit height, width proportional; `size` scales to meters.
  sprite.scale.set(canvas.width / canvas.height, 1, 1)
  return {object: sprite, kind: 'text', dispose: () => disposeObject(sprite)}
}

function buildPlaceholder(): BuiltAsset {
  const geo = new THREE.BoxGeometry(0.3, 0.3, 0.3)
  const mat = new THREE.MeshStandardMaterial({color: 0x8899aa, wireframe: true})
  const object = new THREE.Mesh(geo, mat)
  return {object, kind: 'placeholder', dispose: () => disposeObject(object)}
}

/**
 * Build a renderable object for one anchor_asset config.
 * Returns null for kinds the web preview intentionally skips (audio/video — audio
 * becomes tap-to-play in M2; video is out of MVP scope).
 * Never throws: load failures degrade to a wireframe placeholder.
 */
export async function buildAsset(cfg: RawAnchorAsset): Promise<BuiltAsset | null> {
  // Environment-scan rows (scanned-checkpoint backdrops) are metadata — the
  // `text` field is reference JSON that must never reach the text renderer.
  if (isEnvironmentScan(cfg)) return null
  const kind = resolveAssetKind(cfg)
  const url = assetUrl(cfg.asset?.path)
  try {
    switch (kind) {
      case 'model':
        if (!url) return null
        return await buildModel(url)
      case 'image':
        if (!url) return null
        return await buildImage(url)
      case 'text': {
        const text = (cfg.text ?? '').trim()
        if (!text) return null
        return buildText(text)
      }
      case 'audio':
      case 'video':
        return null // skipped in share preview (see plan §2 / §8.1)
      default:
        return null
    }
  } catch (err) {
    console.warn(`[assets] failed to load ${kind} asset (cfg ${cfg.id}):`, err)
    return buildPlaceholder()
  }
}
